import React, { useState, useEffect } from 'react';
import * as S from './AdvancesStyles';
import { toast } from 'react-toastify';

const fmt = n => (n < 0 ? '−₹' : '₹') + Math.round(Math.abs(n)).toLocaleString('en-IN');

const SettleModal = ({
  show,
  memberId,
  members,
  memberStats,
  onClose,
  onConfirmSettle
}) => {
  const [settleAmount, setSettleAmount] = useState('');
  const [resetMismatch, setResetMismatch] = useState(false);
  const [fullResetAdvance, setFullResetAdvance] = useState(true);
  const [reason, setReason] = useState('');

  const m = members.find(x => x.id === memberId);
  const s = m ? memberStats(memberId) : null;

  // Deliberately depends only on [show, memberId], not on m/s: memberStats(memberId)
  // builds a brand-new plain object on every single render (new reference each time,
  // even when its values haven't changed), so including it here made this effect
  // re-fire on every render — including the one caused by typing into the amount
  // field or ticking a checkbox below — which immediately reset the field right back
  // to its default. The form should only reinitialize when it's actually (re)opened
  // for a member, not on every value it happens to read while doing so.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (m && s) {
      if (m.mode === 'reimbursement') {
        setSettleAmount(s.unpaid);
        setResetMismatch(false);
      } else {
        setSettleAmount(s.balance);
        setFullResetAdvance(true);
      }
      setReason('');
    }
  }, [show, memberId]);

  if (!show || !m || !s) return null;

  const handleSubmit = e => {
    e.preventDefault();
    if (m.mode === 'reimbursement') {
      if (resetMismatch && !reason.trim()) {
        toast.error('Add a reason for the reconciliation — it will show in the audit trail.');
        return;
      }
      onConfirmSettle({
        memberId,
        amount: Number(settleAmount) || 0,
        resetMismatch,
        reason
      });
    } else {
      if (!reason.trim()) {
        toast.error('Please enter a reason for resetting/settling the advance balance.');
        return;
      }
      onConfirmSettle({
        memberId,
        fullReset: fullResetAdvance,
        amount: fullResetAdvance ? s.balance : Number(settleAmount) || 0,
        reason
      });
    }
    onClose();
  };

  return (
    <S.ModalOverlay show={show}>
      <S.ModalContent>
        {m.mode === 'reimbursement' ? (
          <>
            <h2>Settle balance — {m.name}</h2>
            <div
              style={{
                fontSize: '11.5px',
                color: '#6B7280',
                background: '#FAFAFB',
                border: '1px dashed #E7E8EC',
                borderRadius: '8px',
                padding: '9px 11px',
                marginBottom: '14px'
              }}
            >
              Outstanding payable: <strong>{fmt(s.unpaid)}</strong>
              {s.credit > 0 && ` · Credit on file: ${fmt(s.credit)}`}
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>
                    Amount being paid now (₹)
                  </label>
                  <input
                    type="number"
                    value={settleAmount}
                    onChange={e => setSettleAmount(e.target.value)}
                    style={{ width: '100%', padding: '9px 10px', borderRadius: '8px', border: '1px solid #E7E8EC', fontSize: '13px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', fontWeight: 600, color: '#1E2130', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={resetMismatch}
                      onChange={e => setResetMismatch(e.target.checked)}
                      style={{ marginTop: '3px' }}
                    />
                    This is a flat/lump-sum amount that won't exactly match itemized total — reconcile the difference
                  </label>
                </div>

                {resetMismatch && (
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>
                      Reason (required — kept in audit trail)
                    </label>
                    <textarea
                      value={reason}
                      onChange={e => setReason(e.target.value)}
                      rows={2}
                      placeholder="e.g. rounded to flat ₹5,000, negotiated exit settlement..."
                      style={{ width: '100%', padding: '9px 10px', borderRadius: '8px', border: '1px solid #E7E8EC', fontSize: '13px' }}
                      required
                    />
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                <S.Btn type="button" ghost onClick={onClose}>
                  Cancel
                </S.Btn>
                <S.Btn type="submit">Confirm Settlement</S.Btn>
              </div>
            </form>
          </>
        ) : (
          <>
            <h2>Settle / reset — {m.name}</h2>
            <div
              style={{
                fontSize: '11.5px',
                color: '#6B7280',
                background: '#FAFAFB',
                border: '1px dashed #E7E8EC',
                borderRadius: '8px',
                padding: '9px 11px',
                marginBottom: '14px'
              }}
            >
              {s.overdrawn
                ? `Overdrawn by ${fmt(s.converted)} — resetting will top up advance to cover it.`
                : `Current balance held: ${fmt(s.balance)}`}
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', fontWeight: 600, color: '#1E2130', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={fullResetAdvance}
                      onChange={e => setFullResetAdvance(e.target.checked)}
                      style={{ marginTop: '3px' }}
                    />
                    Reset entire balance to ₹0
                  </label>
                </div>

                {!fullResetAdvance && (
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>
                      Amount to settle/write off (₹)
                    </label>
                    <input
                      type="number"
                      value={settleAmount}
                      onChange={e => setSettleAmount(e.target.value)}
                      style={{ width: '100%', padding: '9px 10px', borderRadius: '8px', border: '1px solid #E7E8EC', fontSize: '13px' }}
                    />
                  </div>
                )}

                <div>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>
                    Reason (required — kept in audit trail)
                  </label>
                  <textarea
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    rows={2}
                    placeholder="e.g. field season closed, staff exit, negotiated write-off..."
                    style={{ width: '100%', padding: '9px 10px', borderRadius: '8px', border: '1px solid #E7E8EC', fontSize: '13px' }}
                    required
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                <S.Btn type="button" ghost onClick={onClose}>
                  Cancel
                </S.Btn>
                <S.Btn type="submit">Confirm</S.Btn>
              </div>
            </form>
          </>
        )}
      </S.ModalContent>
    </S.ModalOverlay>
  );
};

export default SettleModal;
